<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { API, HOST } from '@/utils/http'
import { useRoute, useRouter } from 'vue-router'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { RefreshCcw } from 'lucide-vue-next'
import AIReportsList from '@/components/layers/AIReportsList.vue'

const route = useRoute()
const router = useRouter()
const orgId = computed(() => route.params.org_id)
const isLoadingReports = ref(false)
const reports = ref([])
const pollingIntervals = ref(new Map())

// Load all AI reports for this organization
const loadReports = async () => {
  if (!orgId.value) return

  isLoadingReports.value = true
  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/reports`
    })

    reports.value = response.data || []
  } catch (error) {
    console.error('Error loading AI reports:', error)
    toast({
      title: 'Failed to load reports',
      description: 'Unable to load AI reports',
      variant: 'destructive'
    })
  } finally {
    isLoadingReports.value = false
  }
}

// Delete a report
const deleteReport = async (reportCode) => {
  if (!orgId.value) return

  try {
    await API({
      method: 'DELETE',
      url: `${HOST}/org/${orgId.value}/reports/${reportCode}`
    })

    // Refresh the reports list
    await loadReports()

    toast({
      title: 'Report Deleted',
      description: 'AI report has been deleted successfully',
      variant: 'default'
    })

  } catch (error) {
    console.error('Error deleting report:', error)
    toast({
      title: 'Delete Failed',
      description: 'Failed to delete the AI report',
      variant: 'destructive'
    })
  }
}

// Polling functionality for pending reports
const startPollingForReport = (reportCode) => {
  // Don't start polling if already polling this report
  if (pollingIntervals.value.has(reportCode)) {
    return
  }

  const interval = setInterval(async () => {
    try {
      const response = await API({
        method: 'GET',
        url: `${HOST}/org/${orgId.value}/reports/${reportCode}`
      })

      const updatedReport = response.data

      // Update the report in the list
      const index = reports.value.findIndex(r => r.code === reportCode)
      if (index !== -1) {
        reports.value[index] = updatedReport

        // Stop polling if completed or failed
        if (updatedReport.status === 'completed' || updatedReport.status === 'failed') {
          stopPollingForReport(reportCode)

          if (updatedReport.status === 'completed') {
            toast({
              title: 'AI Report Completed!',
              description: `Report has been generated successfully`,
              variant: 'default'
            })
          } else if (updatedReport.status === 'failed') {
            toast({
              title: 'AI Report Failed',
              description: `Report generation failed`,
              variant: 'destructive'
            })
          }
        }
      }
    } catch (error) {
      console.error(`Error polling report ${reportCode}:`, error)
      // Stop polling on error
      stopPollingForReport(reportCode)
    }
  }, 15000) // Poll every 15 seconds

  pollingIntervals.value.set(reportCode, interval)
}

const stopPollingForReport = (reportCode) => {
  const interval = pollingIntervals.value.get(reportCode)
  if (interval) {
    clearInterval(interval)
    pollingIntervals.value.delete(reportCode)
  }
}

const stopAllPolling = () => {
  pollingIntervals.value.forEach((interval) => {
    clearInterval(interval)
  })
  pollingIntervals.value.clear()
}

// Start polling for any pending reports when component loads
const startPollingForPendingReports = () => {
  reports.value.forEach(report => {
    if (report.status === 'pending') {
      startPollingForReport(report.code)
    }
  })
}

// Refresh reports
const refreshReports = async () => {
  await loadReports()
  startPollingForPendingReports()
}

// Load reports when component mounts
onMounted(async () => {
  await loadReports()
  startPollingForPendingReports()
})

// Cleanup polling when component unmounts
onUnmounted(() => {
  stopAllPolling()
})

// Expose refresh function to parent
defineExpose({
  refreshReports
})
</script>

<template>
  <div class="flex items-center justify-end px-1 mb-1">
    <Button
      @click="refreshReports"
      :disabled="isLoadingReports"
      variant="ghost"
      size="sm"
      class="w-8 h-8 rounded-full"
    >
      <Spinner v-if="isLoadingReports" class="h-4 w-4" />
      <RefreshCcw v-else class="h-4 w-4" />
    </Button>
  </div>

  <AIReportsList
    :reports="reports"
    :loading="isLoadingReports"
    @delete="deleteReport"
    @refresh="refreshReports"
  />
</template>
